import { Box } from "@mui/material";
import type { DateTime } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { InHouseTooltip } from "../../../../../components/InHouseTooltip/InHouseTooltip";
import { SystemFlatSwitch } from "../../../../../components/UIToolkit/Clickable/SystemFlatSwitch";
import { SystemSwitch } from "../../../../../components/UIToolkit/Clickable/SystemSwitch";
import { SystemValidationTextField } from "../../../../../components/UIToolkit/Inputs/SystemValidationTextField";
import { DateRangeSelector } from "../../../../../components/UIToolkit/molecules/DateRangeSelector";
import { SettingRow } from "../../../../../components/UIToolkit/molecules/SettingRow";
import { SettingsSection } from "../../../../../components/UIToolkit/molecules/SettingsSection";
import { PrintoutOrientation } from "../../../../../utils/models";
import { DAYS_PER_WEEK } from "../weekScheduleCellResolver";
import type { WeekStaffScheduleExportSettings as Settings } from "./consts";

/**
 * Week-granular export range. `from` is a week's first day and `to` is a week's last day, and the
 * pickers only offer those two weekdays — a block spans exactly seven columns, so a range can't open
 * or close mid-week.
 */
export type ExportDateRange = {
	from: DateTime | undefined;
	to: DateTime | undefined;
	/** Ends of the span the employee actually has weeks for: first week's first day, last week's last day. */
	earliest: DateTime | undefined;
	latest: DateTime | undefined;
	/** Weekdays a range may open / close on (0=Sun … 6=Sat). Sunday-aligned weeks give 0 and 6. */
	boundaryWeekdays: { start: number; end: number } | undefined;
	onFromChange: (date: DateTime) => void;
	onToChange: (date: DateTime) => void;
};

type Props = {
	settings: Settings;
	onChange: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
	/** Job names only exist to disambiguate; a single-job employee has nothing to show. */
	hasMultipleJobs: boolean;
	dateRange: ExportDateRange;
};

const TEST_ID_PREFIX = "week-staff-schedule-export";

/** Whole pages across / down — 0 means "as many as it takes", which is how Excel reads it. */
const MIN_FIT_PAGES = 0;
const MAX_FIT_PAGES = 20;

export const WeekStaffScheduleExportSettings: FC<Props> = ({ settings, onChange, hasMultipleJobs, dateRange }) => {
	const { t } = useTranslation();

	const toggle = (key: keyof Settings & `include${string}`, testId: string, disabled?: boolean, disabledTooltip?: string) => {
		const control = <SystemSwitch checked={settings[key]} onChange={(event) => onChange(key, event.target.checked)} disabled={disabled} dataTestId={testId} />;
		return disabled && disabledTooltip ? <InHouseTooltip title={disabledTooltip}>{<Box component="span">{control}</Box>}</InHouseTooltip> : control;
	};

	const fitPagesField = (key: "fitToWidth" | "fitToHeight", label: string, testId: string) => (
		<SettingRow
			label={label}
			control={
				<SystemValidationTextField
					value={String(settings[key])}
					inputType="number"
					disabled={!settings.fitToPage}
					onChange={(event) => {
						const parsed = Number(event.target.value);
						if (!Number.isInteger(parsed) || parsed < MIN_FIT_PAGES || parsed > MAX_FIT_PAGES) return;
						onChange(key, parsed);
					}}
					formControlSx={{ width: 80 }}
					injectedProps={{ slotProps: { htmlInput: { min: MIN_FIT_PAGES, max: MAX_FIT_PAGES, "data-testid": testId } } }}
				/>
			}
		/>
	);

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
			<SettingsSection
				title={t("profile.schedule.export.range.section")}
				description={t("profile.schedule.export.range.hint")}
				layout="stack"
				dataTestId={`${TEST_ID_PREFIX}-range-section`}
			>
				<DateRangeSelector
					startDate={dateRange.from}
					endDate={dateRange.to}
					onStartDateChange={dateRange.onFromChange}
					onEndDateChange={dateRange.onToChange}
					minDate={dateRange.earliest}
					maxDate={dateRange.latest}
					// Earliest valid end is the end of the start week — a range can't be shorter than one week.
					endMinDate={dateRange.from?.plus({ days: DAYS_PER_WEEK - 1 })}
					shouldDisabledStartDate={(date) => !!dateRange.boundaryWeekdays && date.weekdayISO !== dateRange.boundaryWeekdays.start}
					shouldDisabledEndDate={(date) => !!dateRange.boundaryWeekdays && date.weekdayISO !== dateRange.boundaryWeekdays.end}
					// Flush inside the settings card — the selector's own surface would nest a card in a card.
					removeBackground
					removeBorder
					padding={0}
					startDateTestId={`${TEST_ID_PREFIX}-range-from`}
					endDateTestId={`${TEST_ID_PREFIX}-range-to`}
				/>
			</SettingsSection>

			<SettingsSection title={t("profile.schedule.export.content.section")} layout="stack" dataTestId={`${TEST_ID_PREFIX}-content-section`}>
				<SettingRow label={t("scheduled")} control={toggle("includeScheduled", `${TEST_ID_PREFIX}-toggle-scheduled`)} />
				<SettingRow label={t("actual")} control={toggle("includeActual", `${TEST_ID_PREFIX}-toggle-actual`)} />
				<SettingRow label={t("time.off")} control={toggle("includeTimeOff", `${TEST_ID_PREFIX}-toggle-time-off`)} />
				{/*
					Hidden for now — the only source of a "Prefers" entry is the staff app's self-scheduling
					want-to-work flow, so the toggle reads as dead for most employees. `INCLUDE_PREFERENCES` in
					downloads/weekStaffScheduleExport/persistedSettings.ts holds false, so nothing reaches the
					sheet while this is commented out. To restore: uncomment this row and flip that constant.
					It is deliberately not persisted, so no migration is involved either way.

					<SettingRow label={t("profile.schedule.export.content.preferences")} control={toggle("includePreferences", `${TEST_ID_PREFIX}-toggle-preferences`)} />
				*/}
			</SettingsSection>

			<SettingsSection title={t("profile.schedule.export.details.section")} layout="stack" dataTestId={`${TEST_ID_PREFIX}-details-section`}>
				<SettingRow label={t("location")} control={toggle("includeLocation", `${TEST_ID_PREFIX}-toggle-location`)} />
				<SettingRow
					label={t("job")}
					control={toggle(
						"includeJobName",
						`${TEST_ID_PREFIX}-toggle-job-name`,
						!hasMultipleJobs,
						t("profile.schedule.export.details.job.name.disabled.tooltip"),
					)}
				/>
			</SettingsSection>

			<SettingsSection title={t("profile.schedule.export.page.section")} layout="stack" dataTestId={`${TEST_ID_PREFIX}-page-section`}>
				<SettingRow
					label={t("printout.orientation")}
					control={
						<SystemFlatSwitch
							data-testid={`${TEST_ID_PREFIX}-orientation`}
							options={[
								{ label: t("printout.orientation.landscape"), value: PrintoutOrientation.Landscape },
								{ label: t("printout.orientation.portrait"), value: PrintoutOrientation.Portrait },
							]}
							activeIndexOrValue={settings.orientation}
							onSwitch={(option) => onChange("orientation", option.value)}
						/>
					}
					fullWidthControl
				/>
				<SettingRow
					label={t("printout.fit.to.page")}
					control={
						<SystemSwitch
							checked={settings.fitToPage}
							onChange={(event) => onChange("fitToPage", event.target.checked)}
							dataTestId={`${TEST_ID_PREFIX}-toggle-fit-to-page`}
						/>
					}
				/>
				{fitPagesField("fitToWidth", t("printout.fit.to.width"), `${TEST_ID_PREFIX}-fit-to-width`)}
				{fitPagesField("fitToHeight", t("printout.fit.to.height"), `${TEST_ID_PREFIX}-fit-to-height`)}
			</SettingsSection>
		</Box>
	);
};
